import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import { CartT, LineItemType } from '@lambda/apis/src/cart/types';
import { WebsiteSchema } from '@/components/homepage/WebsiteSchema';
import { getShortenCountryName } from '@/lib/use-origin-country';
import i18n from '@/settings/i18n';
import { useCartContext } from '@/utils/hooks/cart/useCartContext';
import RightSection from './RightSection';
import LeftSection from './LeftSection';
import Loading from '@/components/commons/Loading';
import EmptyCart from '@/components/cart/EmptyCart';
import useOutOfStock from '@/utils/hooks/cart/useOutOfStock';
import { ToBeRemovedT } from '@/utils/hooks/cart/types';
import useAddons from './hooks/useAddons';
import useDeliveryEstimates from './hooks/useDeliveryEstimates';
import {
  trackCartUpdateModalDismissed,
  trackCartUpdateSimilarOffersClicked,
  trackCartViewed,
} from '@/lib/analytics/trackCart';
import OutOfStockPanel from './LeftSection/OutOfStockPanel';
import { RecommendationsTrackingProvider } from './hooks/useRecommendationsTracking';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import { isPurchaseFlowEnabled } from '@/lib/purchase-flow';
import CartRecommendedAccessories from './CartRecommendedAccessories';

const t = i18n.init('cart');

// Roughly the height of StickyCheckoutBar, used to keep the footer and the
// cookie banner clear of it.
const STICKY_BAR_OFFSET = '96px';

const NoZoomViewport = () => (
  <Head>
    <meta
      name="viewport"
      content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
    />
  </Head>
);

const LoadingCart = () => (
  <>
    <NoZoomViewport />
    <WebsiteSchema
      title={t`Your Shopping Cart - Reebelo ${{
        country: getShortenCountryName(),
      }}`}
      description={t`Review your cart, update quantities, and proceed to secure checkout on Reebelo. Get the best deals on refurbished tech with warranty and fast shipping.`}
    />
    <div className="reebelo-container w-full pb-10 pt-5">
      <div className="flex items-center justify-center py-12 md:py-32">
        <Loading />
      </div>
    </div>
  </>
);

const CartPageLayout = ({
  cartLinesToBeUpdated,
  initialCart,
}: {
  cartLinesToBeUpdated: ToBeRemovedT[];
  initialCart: CartT | null;
}) => {
  const { cart, cartInitialized, cartLines, initializeCart } = useCartContext();
  const { warranties, expressShippingOffers, categoryWarranties } = useAddons();
  const deliveryEstimates = useDeliveryEstimates();
  const [reebeloCareChecked, setReebeloCareChecked] = useState(false);

  const hasReebeloCareInCart = useMemo(
    () => cartLines.some((line) => line.type === LineItemType.WARRANTY),
    [cartLines],
  );

  useEffect(() => {
    if (hasReebeloCareInCart) {
      setReebeloCareChecked(true);
    }
  }, [hasReebeloCareInCart]);

  const totalReebeloCarePrice = useMemo(
    () =>
      Object.entries(warranties).reduce(
        (acc, [lineId, { productWarranty }]) => {
          const line = cartLines.find((l) => l.id === lineId);

          return acc + productWarranty.price * (line?.quantity ?? 0);
        },
        0,
      ),
    [warranties, cartLines],
  );

  const { customer } = useCustomerContext();
  // In the purchase flow a plan is a cart line as soon as it's chosen (on the
  // line item), so it's already in the cart's own totals: there's no pending
  // plan to add on top.
  const reebeloCareTotal =
    reebeloCareChecked && !isPurchaseFlowEnabled(customer?.email)
      ? totalReebeloCarePrice
      : 0;
  const reebeloCareSubtotal = Math.round(reebeloCareTotal * 100);

  useEffect(() => {
    if (initialCart) {
      initializeCart(initialCart);
    }

    if (initialCart) {
      initializeCart(initialCart);
    }
  }, []);

  const { removedCartLines, hasOutOfStockItems, onDismiss } = useOutOfStock({
    toBeRemoved: cartLinesToBeUpdated,
  });

  useEffect(() => {
    if (!cart) {
      return;
    }

    trackCartViewed(cart);
  }, [cart?.id]);

  // The sticky checkout bar is fixed over the bottom of the page on mobile:
  // pad the page shell so the footer can scroll clear of it, and lift the
  // cookie banner above it.
  useEffect(() => {
    if (typeof document === 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }

    const root = document.documentElement;

    root.style.setProperty('--sticky-bar-offset', STICKY_BAR_OFFSET);
    root.style.setProperty('--cookie-banner-offset', STICKY_BAR_OFFSET);

    return () => {
      root.style.removeProperty('--sticky-bar-offset');
      root.style.removeProperty('--cookie-banner-offset');
    };
  }, []);

  const trackRemovedProducts = (typeId: string) =>
    removedCartLines
      .filter((item) => item.line.typeId === typeId)
      .map(({ line }) => ({
        line_item_id: line.id,
        offer_pk: line.typeId,
        vendor: line.vendor,
        sku: line.sku,
        psku: line.psku,
        name: line.title,
      }));

  const onDismissClick = (typeId: string) => {
    if (cart) {
      trackCartUpdateModalDismissed({
        cart,
        properties: {
          cart_id: cart.id,
          store: cart.store,
          products: trackRemovedProducts(typeId),
        },
      });
    }

    onDismiss(typeId);
  };

  const onSimilarOffersClick = (typeId: string) => {
    if (cart) {
      trackCartUpdateSimilarOffersClicked({
        cart,
        properties: {
          cart_id: cart.id,
          store: cart.store,
          products: trackRemovedProducts(typeId),
        },
      });
    }

    onDismiss(typeId);
  };

  if (cart == null && !cartInitialized) {
    return <LoadingCart />;
  }

  if (cartLines.length === 0 && cartInitialized) {
    return (
      <>
        {hasOutOfStockItems && (
          <div className="reebelo-container w-full pt-4">
            <OutOfStockPanel
              removedCartLines={removedCartLines}
              hasOutOfStockItems={hasOutOfStockItems}
              onDismiss={onDismissClick}
              onSimilarOffers={onSimilarOffersClick}
              className=""
            />
          </div>
        )}
        <EmptyCart cartSoldOut={removedCartLines.length !== 0} />
      </>
    );
  }

  return (
    <>
      <NoZoomViewport />
      <WebsiteSchema
        title={t`Your Shopping Cart - Reebelo ${{
          country: getShortenCountryName(),
        }}`}
        description={t`Review your cart, update quantities, and proceed to secure checkout on Reebelo. Get the best deals on refurbished tech with warranty and fast shipping.`}
      />
      <RecommendationsTrackingProvider cart={cart}>
        <div
          className={classNames(
            'reebelo-container relative gap-5 py-4 text-gray-700 lg:grid lg:grid-cols-12 lg:bg-gray-300',
            isPurchaseFlowEnabled(customer?.email) ? 'bg-gray-300' : 'bg-white',
          )}
        >
          <LeftSection
            hasOutOfStockItems={hasOutOfStockItems}
            onDismiss={onDismiss}
            removedCartLines={removedCartLines}
            expressShippingOffers={expressShippingOffers}
            deliveryEstimates={deliveryEstimates}
            reebeloCareSubtotal={reebeloCareSubtotal}
            warranties={warranties}
            categoryWarranties={categoryWarranties}
          />
          <RightSection
            warranties={warranties}
            reebeloCareChecked={reebeloCareChecked}
            setReebeloCareChecked={setReebeloCareChecked}
            totalReebeloCarePrice={totalReebeloCarePrice}
            reebeloCareSubtotal={reebeloCareSubtotal}
          />
        </div>
        {/* After the grid, not in it: the summary is sticky within the grid,
            and would otherwise ride down over these cards. */}
        {isPurchaseFlowEnabled(customer?.email) && (
          <div className="bg-gray-300">
            {/* Divider inside the container, so it's as wide as the cart.
                ~40px either side of it: the grid's own 16px bottom padding +
                24px above, and 36px + the heading's half-leading below. */}
            <div className="reebelo-container pb-10">
              <CartRecommendedAccessories className="mt-6 border-t border-[#E6E7EB] pt-9" />
            </div>
          </div>
        )}
      </RecommendationsTrackingProvider>
    </>
  );
};

export default CartPageLayout;
